import cx from 'classnames';
import type { FunctionComponent, ReactNode } from 'react';

import { useCardId } from '@trello/id-context';
import { smallestPreview } from '@trello/image-previews';
import { ExternalLinkIcon } from '@trello/nachos/icons/external-link';
import { Tooltip } from '@trello/nachos/tooltip';
import { RouterLink } from '@trello/router/router-link';

import { useMirrorCardBoardChipFragment } from './MirrorCardBoardChipFragment.generated';
import { MirrorCardBoardIcon } from './MirrorCardBoardIcon';

import styles from './MirrorCardBoardChip.less';

interface ConditionalRouterLinkWrapperProps {
  children: ReactNode;
  isPaid: boolean;
  boardUrl: string;
  isCompact: boolean;
}
/**
 * A component that conditionally adds a RouterLink depending
 * if in a free workspace or not.
 */
const ConditionalRouterLinkWrapper: FunctionComponent<
  ConditionalRouterLinkWrapperProps
> = ({ children, isPaid, boardUrl, isCompact }) => {
  if (!isPaid) {
    return (
      <div
        className={cx(styles.mirrorCardBoardChip, styles.free, styles.compact)}
        tabIndex={-1}
      >
        {children}
      </div>
    );
  }

  return (
    <RouterLink
      href={boardUrl}
      className={cx(
        styles.mirrorCardBoardChip,
        styles.paid,
        isCompact ? styles.compact : styles.expanded,
      )}
      onClick={(e) => {
        e.stopPropagation();
      }}
      tabIndex={-1}
    >
      {children}
    </RouterLink>
  );
};
interface MirrorCardBoardChipProps {
  isPaid: boolean;
  isCompact: boolean;
}

export const MirrorCardBoardChip: FunctionComponent<
  MirrorCardBoardChipProps
> = ({ isPaid, isCompact }) => {
  const cardId = useCardId();

  const { data: card } = useMirrorCardBoardChipFragment({
    from: { id: cardId },
  });

  const board = card?.board;
  const listName = card?.list?.name;

  // some old boards have not gone through image scaling,
  // so <board response>.prefs.backgroundImageScaled === null
  const { url: backgroundUrl } = smallestPreview(
    board?.prefs?.backgroundImageScaled,
  ) ?? { url: board?.prefs?.backgroundImage || undefined };
  const backgroundColor = board?.prefs?.backgroundColor ?? '';

  if (!board) {
    return;
  }

  return (
    <ConditionalRouterLinkWrapper
      boardUrl={board.url}
      isCompact={isCompact}
      isPaid={isPaid}
    >
      <Tooltip
        content={isPaid ? `${board?.name}: ${listName}` : `${board?.name}`}
        position="top"
        delay={600}
      >
        <div className={styles.boardChipContents}>
          <MirrorCardBoardIcon
            backgroundColor={backgroundColor}
            backgroundImage={backgroundUrl}
            isCompact={isCompact}
            isPaid={isPaid}
          />
          <div className={styles.names}>
            <div className={styles.boardName}>
              <span className={styles.boardNameText}>{board?.name}</span>
              {isPaid && (
                <span className={styles.externalLinkIcon}>
                  <ExternalLinkIcon size="small" />
                </span>
              )}
            </div>
            {isPaid && !isCompact && (
              <span className={styles.listName}>{listName}</span>
            )}
          </div>
        </div>
      </Tooltip>
    </ConditionalRouterLinkWrapper>
  );
};
